import { track } from "@connect/observability";
import type { PostMessageClient } from "@connect/post-message/client";
import type { BuyButtonFrameMessageMap } from "@connect/post-message/protocol";
import type { BuyButtonConfig } from "../../config";
import type { SignedQuote } from "../../../../lib/buy/quote.types";
import { runCreate } from "../../lib/run-create";
import { runPreCheck } from "../../lib/run-pre-check";

// Card has no payment sheet, so its lifecycle is a step shorter than a wallet's.
export type CardPhase = "idle" | "checking" | "creating" | "done";

export interface RunCardPaymentParams {
  quote: SignedQuote;
  config: BuyButtonConfig;
  /** The saved card to charge, read off the quote's payment method. */
  cardId: string;
  pm: PostMessageClient<BuyButtonFrameMessageMap>;
  isCancelled: () => boolean;
  setPhase: (phase: CardPhase) => void;
  busy: { current: boolean };
  /** Survives taps: a KYC step-up is only offered once. */
  kycStepUp: { current: boolean };
  /** Overridable for tests. */
  preCheck?: typeof runPreCheck;
  /** Overridable for tests. */
  create?: typeof runCreate;
}

const PAYMENT_METHOD = "card";

function enterPhase(
  phase: CardPhase,
  busy: RunCardPaymentParams["busy"],
  setPhase: RunCardPaymentParams["setPhase"],
) {
  busy.current = phase !== "idle";
  setPhase(phase);
}

/**
 * Card is backend-driven: pre-check, then create. There is no sheet and no
 * token -- the quote's saved card id is all the create needs. The challenge,
 * recheck and completion handling all come from the shared `runCreate`.
 */
export async function runCardPayment({
  quote,
  config,
  cardId,
  pm,
  isCancelled,
  setPhase,
  busy,
  kycStepUp,
  preCheck = runPreCheck,
  create = runCreate,
}: RunCardPaymentParams): Promise<void> {
  // Set synchronously so a double tap can't create two transactions.
  if (busy.current) {
    return;
  }
  busy.current = true;

  pm.send("buttonPressed");
  track.info("connect.buy_button.button_pressed", { paymentMethod: PAYMENT_METHOD });

  // Card is gated to authenticated customers at the button, so there is no guest path here.
  enterPhase("checking", busy, setPhase);
  const check = await preCheck({ quote, config, pm, paymentMethod: PAYMENT_METHOD });
  if (isCancelled()) {
    return;
  }
  // After a challenge the customer taps again, so free the button.
  if (check.outcome !== "pass") {
    enterPhase("idle", busy, setPhase);
    return;
  }

  enterPhase("creating", busy, setPhase);
  const outcome = await create({
    quote,
    config,
    pm,
    instrument: { kind: "card", cardId, challengeToken: check.challengeToken },
    isCancelled,
    kycStepUp,
  });
  // A create-time challenge frees the button so the customer can tap again; otherwise we're done.
  enterPhase(outcome === "challenge" ? "idle" : "done", busy, setPhase);
}
